import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Activity, Send, Sparkles, User, Stethoscope, CalendarDays } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WidgetChatPage() {
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get('tenant');
  const [session, setSession] = useState(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [config, setConfig] = useState(null);
  const [started, setStarted] = useState(false);
  const [patientName, setPatientName] = useState('');
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (tenantId) {
      axios.get(`${API_URL}/widget/config/${tenantId}`).then(r => setConfig(r.data)).catch(() => {});
    }
  }, [tenantId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    if (!sending) setTimeout(() => inputRef.current?.focus(), 100);
  }, [session?.messages, sending]);

  const startChat = async () => {
    try {
      const res = await axios.post(`${API_URL}/widget/sessions`, { tenant_id: tenantId, patient_name: patientName || 'Website Visitor' });
      setSession(res.data);
      setStarted(true);
    } catch { alert('Failed to start chat'); }
  };

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!message.trim() || sending || !session) return;
    setSending(true);
    const msg = message;
    setMessage('');
    setSession(prev => ({ ...prev, messages: [...prev.messages, { role: 'user', content: msg, timestamp: new Date().toISOString() }] }));
    try {
      const res = await axios.post(`${API_URL}/widget/sessions/${session.id}/messages`, { message: msg });
      setSession(res.data);
    } catch {
      setSession(prev => ({ ...prev, messages: prev.messages.slice(0, -1) }));
      setMessage(msg);
    } finally { setSending(false); }
  };

  const primaryColor = config?.primary_color || '#0284c7';
  const clinicName = config?.clinic_name || 'engagingly.ai';
  const assessment = session?.triage_summary;
  const doctors = session?.recommended_doctors || [];

  const cleanContent = (t) => (t || '').replace(/```json[\s\S]*?```/g, '').trim();

  if (!tenantId) return <div className="flex items-center justify-center h-screen bg-white text-slate-400 text-sm">Invalid widget configuration</div>;

  return (
    <div className="flex flex-col h-screen bg-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <div className="px-4 py-3 flex items-center gap-3 border-b" style={{ backgroundColor: primaryColor }}>
        <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
          <Activity className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-white text-sm font-semibold">{clinicName}</p>
          <p className="text-white/70 text-xs">AI Health Assistant</p>
        </div>
      </div>

      {!started ? (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-xs">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${primaryColor}15` }}>
              <Sparkles className="w-7 h-7" style={{ color: primaryColor }} />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Hi there!</h3>
            <p className="text-sm text-slate-500 mb-5">I'm your AI health assistant. I can help assess your symptoms and recommend the right care.</p>
            <input value={patientName} onChange={e => setPatientName(e.target.value)} placeholder="Your name (optional)" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-3 focus:outline-none focus:ring-2" style={{ '--tw-ring-color': primaryColor }} data-testid="widget-patient-name" />
            <button onClick={startChat} className="w-full py-2.5 rounded-lg text-white text-sm font-medium transition-opacity hover:opacity-90" style={{ backgroundColor: primaryColor }} data-testid="widget-start-btn">
              Start Health Check
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="widget-messages">
            {session?.messages?.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className="flex items-start gap-2 max-w-[85%]">
                  {msg.role === 'assistant' && (
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: `${primaryColor}20` }}>
                      <Sparkles className="w-3 h-3" style={{ color: primaryColor }} />
                    </div>
                  )}
                  <div className={`rounded-2xl px-3.5 py-2.5 text-sm ${msg.role === 'user' ? 'text-white rounded-br-md' : 'bg-slate-100 text-slate-800 rounded-bl-md'}`}
                    style={msg.role === 'user' ? { backgroundColor: primaryColor } : {}}>
                    {msg.role === 'assistant' ? (
                      <div className="prose prose-sm max-w-none"><ReactMarkdown>{cleanContent(msg.content)}</ReactMarkdown></div>
                    ) : <p>{msg.content}</p>}
                  </div>
                  {msg.role === 'user' && <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0 mt-1"><User className="w-3 h-3 text-slate-500" /></div>}
                </div>
              </div>
            ))}

            {assessment && (
              <div className="p-3 rounded-xl border-2 bg-white" style={{ borderColor: primaryColor + '40' }} data-testid="widget-assessment">
                <p className="text-xs font-bold uppercase mb-1" style={{ color: primaryColor }}>Assessment: {assessment.urgency_level?.replace('_', ' ')}</p>
                <p className="text-xs text-slate-600 mb-2">{assessment.summary || assessment.recommended_action}</p>
                {assessment.primary_symptoms?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">{assessment.primary_symptoms.map((s, i) => <span key={i} className="text-[10px] px-2 py-0.5 bg-slate-100 rounded-full">{s}</span>)}</div>
                )}
                <p className="text-[10px] text-slate-400 italic">AI-assisted triage, not a medical diagnosis.</p>
              </div>
            )}

            {doctors.length > 0 && (
              <div className="space-y-2" data-testid="widget-doctors">
                <p className="text-xs font-semibold text-slate-700">Recommended Doctors:</p>
                {doctors.map(d => (
                  <div key={d.id} className="flex items-center gap-2.5 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
                      <Stethoscope className="w-4 h-4" style={{ color: primaryColor }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-900">{d.name}</p>
                      <p className="text-[10px] text-slate-500">{d.specialty}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {sending && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                    <Sparkles className="w-3 h-3" style={{ color: primaryColor }} />
                  </div>
                  <div className="bg-slate-100 rounded-2xl px-4 py-2.5 flex gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" />
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="px-4 py-3 border-t">
            <form onSubmit={sendMsg} className="flex gap-2">
              <input ref={inputRef} value={message} onChange={e => setMessage(e.target.value)} placeholder="Describe your symptoms..." disabled={sending}
                className="flex-1 px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2" style={{ '--tw-ring-color': primaryColor }} data-testid="widget-input" />
              <button type="submit" disabled={!message.trim() || sending} className="px-4 py-2.5 rounded-lg text-white disabled:opacity-50" style={{ backgroundColor: primaryColor }} data-testid="widget-send">
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
